import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { ToastService } from '../../../../../shared/services/toast.service';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketPipeline } from '../../../models/tickets-config.model';

@Component({
  selector: 'app-tickets-pipelines-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  template: `
    <div class="p-6 space-y-4">
      @if (error) {
        <div class="p-3 bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 rounded-lg text-sm text-error-700 dark:text-error-400">{{ error }}</div>
      }
      <form [formGroup]="form" (ngSubmit)="guardar()" class="space-y-4">
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nombre <span class="text-error-500">*</span>
          </label>
          <input type="text" formControlName="name" maxlength="150"
            class="w-full px-2.5 py-1.5 text-sm border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500"
            [class.border-error-500]="hasError('name')"
            [class.border-gray-300]="!hasError('name')" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Descripción</label>
          <textarea formControlName="description" rows="2"
            class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"></textarea>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Orden</label>
          <input type="number" formControlName="sortOrder"
            class="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500" />
        </div>
        <div class="space-y-2">
          <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" formControlName="isDefault" class="rounded border-gray-300 dark:border-gray-600" />
            Default (solo uno por business)
          </label>
          <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" formControlName="isActive" class="rounded border-gray-300 dark:border-gray-600" />
            Activo
          </label>
        </div>
        <div class="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
          <app-button variant="outline" type="button" (btnClick)="cancelar()">Cancelar</app-button>
          <app-button type="button" [disabled]="saving" (btnClick)="guardar()">
            @if (saving) { Guardando… } @else { {{ isEditing ? 'Actualizar' : 'Guardar' }} }
          </app-button>
        </div>
      </form>
    </div>
  `
})
export class TicketsPipelinesFormComponent implements OnDestroy {
  @Output() saved = new EventEmitter<TicketPipeline>();
  @Output() cancelled = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private configApi = inject(TicketsConfigApiService);
  private toast = inject(ToastService);

  isEditing = false;
  itemId: number | null = null;
  saving = false;
  error: string | null = null;
  private originalName = '';

  form: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(150)]],
    description: [''],
    sortOrder: [0],
    isDefault: [false],
    isActive: [true]
  });

  private readonly destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reset(): void {
    this.isEditing = false;
    this.itemId = null;
    this.error = null;
    this.saving = false;
    this.form.reset({ name: '', description: '', sortOrder: 0, isDefault: false, isActive: true });
  }

  loadPipeline(id: number): void {
    this.isEditing = true;
    this.itemId = id;
    this.configApi.getPipelineById(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.form.patchValue({
          name: item.name,
          description: item.description ?? '',
          sortOrder: item.sortOrder ?? 0,
          isDefault: item.isDefault,
          isActive: item.isActive
        });
        this.originalName = item.name;
      },
      error: (err) => { this.error = err.error?.message || 'Error al cargar pipeline'; }
    });
  }

  hasError(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  }

  guardar(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.saving = true;
    this.error = null;
    const raw = this.form.value;
    const req = {
      name: raw.name,
      description: raw.description || undefined,
      sortOrder: Number(raw.sortOrder ?? 0),
      isDefault: !!raw.isDefault,
      isActive: !!raw.isActive
    };
    const obs = this.isEditing && this.itemId != null
      ? this.configApi.updatePipeline(this.itemId, req)
      : this.configApi.createPipeline(req);

    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.saving = false;
        if (this.isEditing) this.toast.updated('Pipeline',
          this.originalName !== (raw.name?.trim() || '')
            ? `Se actualizó "${this.originalName}" → "${raw.name?.trim() || ''}".`
            : `Se actualizó "${raw.name?.trim() || ''}" correctamente.`);
        else this.toast.created('Pipeline', `Se creó "${raw.name?.trim() || ''}" correctamente.`);
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'Error al guardar el pipeline';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  cancelar(): void { this.cancelled.emit(); }
}
